import { FC, useEffect, useState } from 'react';
import { ToggleSwitch, Button, PrimaryButton, Input, InputPhoneInt, DatePicker, TextArea } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { styled } from '@mui/system';
import { COLORS, instLvlOptions, usStateOptions, KEY_OTHER, KEY_US_GOV, TYPE_OPTIONS, APP_URLS } from 'src/constants';

import { Typography } from 'src/components';
import { useAuth } from 'src/contexts';
import { IAddress, ValidationError, reduceAddress, IPhone } from 'src/types';
import { get } from 'lodash';

import { AddressFields } from 'src/generic/component/address';
import { FieldLabel, Title } from 'src/generic/component';
import { Checkboxes, Checkbox, Dropdown } from 'src/generic/component/form';

import { ERRORS_EMPTY, ErrorManager } from 'src/types/validation';
import { ContactFields } from 'src/generic/component/contact';
import { IContact } from 'src/types/org/common/contactSchema';
import { IRequestMeta } from 'src/types/org/request';
import { IProgramDeactivation } from 'src/types/org/common/appFormSchema';
import { IData } from 'src/types/org/common/dataSchema';
import { IDIRequest, validate } from 'src/types/org/di/diRequestSchema';
import { nullAndEmptyStringToEmptyObj, nullAndEmptyStringToundefined } from 'src/utils';
import { submitDIUpdateForm } from '../apis/submitUpdate';
import { useNavigate } from 'react-router-dom';
import { ChangeManager } from 'src/generic/utils/form';

const StyledGrid = styled(Grid)`
  padding: 20px 0;
  border-bottom: 1px solid ${COLORS.borderNavy};

  &:first-of-type {
    border-top: 1px solid ${COLORS.borderNavy};
  }
`;

interface FormProps {
  request: Partial<IDIRequest<IData>>;
  readOnly?: boolean;
  source: string;
  searchParams: object;
  onComplete: (update: IDIRequest<IData>) => void;
  onCancel?: () => void;
}

const FieldRow = styled(Grid)`
  margin-bottom:10px;
`;

const FieldData = styled(Grid)`
  margin-bottom:2px;
`;

interface IChildProp {
  children?: React.ReactNode;
}

const FieldValue: FC<IChildProp> = ({ children }) => {
  return <span style={{ fontWeight: '500' }}>{children}</span>;
};

const getInitial = (update: Partial<IData>): Partial<IData> => {
  const { orgId, instLvlCd, name, nameShort, nameAbbr, orgTypeCd, orgSubTypeCd, email, website, phone, fax, visitingAddress, parentOrgName, parentOrgId } = update.org || {};
  const { diCode, apEligibilityInd, apContact, satEligibilityInd, satContact, apAddress } = update.di || {};
  const { isMerge, merge, submitterComment } = update.appForm || {};

  return {
    org: {
      parentOrgName,
      parentOrgId,
      orgId,
      instLvlCd,
      name,
      nameShort,
      nameAbbr,
      orgTypeCd,
      orgSubTypeCd,
      email,
      website,
      phone: { formatted: phone?.formatted },
      fax: { formatted: fax?.formatted },
      visitingAddress: reduceAddress({ ...visitingAddress }),
    },
    di: {
      diCode,
      diMergedCode: merge?.diCode,
      apEligibilityInd,
      apContact,
      apAddress: reduceAddress({ ...apAddress }),
      satEligibilityInd,
      satContact
    },
    appForm: {
      isMerge,
      merge,
      submitterComment
    }
  };
};

const isFieldSet = (field: string, data: any): boolean => {
  const path = field.split('.');
  let existing: any = data || {};
  for (const fragment of path) {
    if (fragment in existing) {
      existing = get(existing, fragment);
    } else {
      return false;
    }
  }
  return true;
};

const extractCountry = (phone: Partial<IPhone>) => {
  const cc = phone.phoneCc;
  const formatted = phone.formatted;
  if (cc && cc !== '') return cc;

  if (formatted) {
    const parts = formatted.split(' ');
    if (parts[0].includes('+') && parts[0].length <= 3) {
      return parts[0].replace('+', '');
    }
  }

  return 'US';
};

export const DiUpdateForm = ({ readOnly, request, source, searchParams, onComplete, onCancel }: FormProps) => {
  const { userName, userEmail } = useAuth();
  const navigate = useNavigate();
  const { data, original, changes, meta } = request;

  const update: Partial<IData> = data || {};
  const initial = original || getInitial(update);

  let defaultCountry = update.org?.phone ? extractCountry(update.org?.phone) : undefined;
  let formattedPhone = update.org?.phone ? (update.org?.phone.formatted || '') : '';
  let formattedFax = update.org?.fax ? (update.org?.fax.formatted || '') : '';
  const visiting = { ...update.org?.visitingAddress };

  const [bannerColor, setBannerColor] = useState('#FF0000');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>("");

  const [name, setName] = useState<string | undefined>(update.org?.name || undefined);
  const [email, setEmail] = useState<string | undefined>(update.org?.email || undefined);
  const [phone, setPhone] = useState<string | undefined>(formattedPhone || undefined);
  const [fax, setFax] = useState<string | undefined>(formattedFax || undefined);
  const [website, setWebsite] = useState<string | undefined>(update.org?.website || undefined);
  const [visitingAddress, setVisitingAddress] = useState<Partial<IAddress>>(visiting);
  const [instType, setInstType] = useState<string[]>(update.appForm?.instType || []);
  const [instTypeOther, setInstTypeOther] = useState<string | undefined>(update.appForm?.instTypeOther || undefined);
  const [instLvlCd, setInstLvlCd] = useState<number | undefined>(update.org?.instLvlCd || undefined);
  const [stateRep, setStateRep] = useState<string | undefined>(update.appForm?.stateRep || undefined);
  const [congressDistrict, setCongressDistrict] = useState<string | undefined>(update.appForm?.congressDistrict || undefined);
  const [incumbent, setIncumbent] = useState<string | undefined>(update.appForm?.incumbent || undefined);
  const [satEligibilityInd, setSatEligibilityInd] = useState<boolean>(!!update.di?.satEligibilityInd);
  const [satContact, setSatContact] = useState<Partial<IContact> | undefined>(nullAndEmptyStringToEmptyObj(update.di?.satContact));
  const [satDisclaimer, setSatDisclaimer] = useState<boolean>(!!update.appForm?.satProgramDeactivation?.deactivateDisclaimer);
  const [satClosureDate, setSatClosureDate] = useState<string | undefined>(update.appForm?.satProgramDeactivation?.closureDate || undefined);
  const satProgramDeactivation: IProgramDeactivation = {
    closureDate: satClosureDate,
    deactivateDisclaimer: satDisclaimer
  };
  const [apEligibilityInd, setApEligibilityInd] = useState<boolean>(!!update.di?.apEligibilityInd);
  const [apContact, setApContact] = useState<Partial<IContact> | undefined>(nullAndEmptyStringToEmptyObj(update.di?.apContact));
  const [apAddress, setApAddress] = useState<Partial<IAddress> | undefined>(nullAndEmptyStringToundefined(update.di?.apAddress));
  const [apDisclaimer, setApDisclaimer] = useState<boolean>(!!update.appForm?.apProgramDeactivation?.deactivateDisclaimer);
  const [apClosureDate, setApClosureDate] = useState<string | undefined>(update.appForm?.apProgramDeactivation?.closureDate || undefined);
  const apProgramDeactivation: IProgramDeactivation = {
    closureDate: apClosureDate,
    deactivateDisclaimer: apDisclaimer
  };
  const [isMerge, setMerge] = useState<boolean>(!!update.appForm?.isMerge);
  const [mergedDICd, setMergedDICd] = useState<string | undefined>(update.appForm?.merge?.diCode || undefined);
  const [mergeName, setMergeName] = useState<string | undefined>(update.appForm?.merge?.name || undefined);
  const [mergeAddress, setMergeAddress] = useState<Partial<IAddress> | undefined>(update.appForm?.merge?.address);

  //TODO: should clear everytime user re-submits
  const [disclaimer, setDisclaimer] = useState<boolean>(!!meta?.disclaimer);
  const [submitter, setSubmitter] = useState<string>(meta?.submitter || '');
  const [submitterComment, setSubmitterComment] = useState<string | undefined>(update.appForm?.submitterComment);

  const getData = (): Partial<IData> => {
    const hasMergeName = mergeName && mergeName.length > 0;
    const hasMergeCode = mergedDICd && mergedDICd.length > 0;

    const merge = isMerge ? {
      name: hasMergeName ? mergeName : undefined,
      address: hasMergeName && mergeAddress ? reduceAddress(mergeAddress) : undefined,
      diCode: hasMergeCode ? mergedDICd : undefined
    } : undefined;

    //passing along
    const { nameShort, nameAbbr, orgTypeCd, orgSubTypeCd } = update.org || {};

    return {
      org: {
        orgId: update.org?.orgId,
        name,
        nameShort,
        nameAbbr,
        orgTypeCd,
        orgSubTypeCd,
        instLvlCd,
        email,
        website,
        phone: {
          formatted: phone
        },
        fax: fax ? {
          formatted: fax
        } : undefined,
        visitingAddress: reduceAddress(visitingAddress),
      },
      di: {
        diCode: update.di?.diCode,
        diMergedCode: mergedDICd,
        apEligibilityInd,
        apContact,
        apAddress: apAddress ? reduceAddress(apAddress) : undefined,
        satEligibilityInd,
        satContact,
      },
      appForm: {
        instType,
        instTypeOther,
        stateRep,
        congressDistrict,
        incumbent,
        apProgramDeactivation,
        satProgramDeactivation,
        isMerge,
        merge,
        submitterComment,
      }
    };
  };

  const getMeta = (): Partial<IRequestMeta> => {
    return {
      creator: userName,
      creatorEmail: userEmail,
      disclaimer,
      submitter
    };
  };

  /**
    * Error and Validation Management
    */
  let errors = ErrorManager(ERRORS_EMPTY);
  let request2: IDIRequest<IData> | undefined = undefined;
  let validated: boolean = false;

  const { getChangeColor } = ChangeManager<IData>(original || {}, data || {});

  try {
    const updateObj = {
      userId: userName,
      recordCategoryType: 'di',
      data: getData(),
      meta: getMeta()
    };
    // console.log('updateObj: ', updateObj);
    request2 = validate(updateObj, 'update', initial);
    validated = true;
  } catch (error: any) {
    if (error instanceof ValidationError) {
      errors = ErrorManager(error.fieldErrors);
    }
  }
  const { getError, hasError, getFieldError, getErrorMessages } = errors;

  const submit = async () => {
    setBannerColor('#324DC7');
    setSubmitting(true);

    try {
      if (validated) {
        const response = await submitDIUpdateForm(request2);
        onComplete(response);
      } else {
        throw new Error("There were invalid fields still in the form. Look for errors near your fields");
      }
    } catch (error: any) {
      setErrorMessage(error?.message || error);
      console.log(error);
    }
    // Perform Redirect
    setSubmitting(false);
  };

  const getCurrent = (field: string): any => {
    return get(original || {}, field);
  };

  const isChanged = (field: string): boolean => {
    let updates: any = changes || {};
    return isFieldSet(field, updates);
  };

  const onFieldChange = () => { };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => { }, [initial]);

  const fieldMap = [
    { key: '', name: '' },
    { key: 'diff', name: "Not Updated" },
    { key: 'meta.creator', name: 'Creator' },
    { key: 'meta.creatorEmail', name: 'Creator Email' }
  ];

  return <form onSubmit={submit}>
    {errorMessage &&
      <div className="cb-notification" aria-labelledby="apricot_notification_542" role="region" style={{ marginBottom: '20px' }}>
        <div className="cb-notification-container">
          <div className="cb-notification-header">
            <i className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
            <h4 id="apricot_notification_542" className="cb-notification-title"><strong>Errors Occurred on the Update</strong></h4>
          </div>
          <div className="cb-notification-content">
            {errorMessage}
            <div style={{ whiteSpace: 'pre-line' }}>
              {fieldMap.map((field, i) => { return hasError(field.key) ? <li key={`update-field-error-${i}`}>{field.name ? `${field.name}:` : ``} {getErrorMessages(field.key).join("\n")}</li> : ''; })}
            </div>
          </div>
        </div>
      </div>
    }
    <Grid item xs={12}>
      <div style={{ marginBottom: '10px' }}>
        <h3>Update Request Form</h3>
        <Typography variant="body1" style={{ fontWeight: 700, color: bannerColor }}>
          Update requests must be submitted by an authorized user. Authorized users are full-time employees listed in the staff directory on your institution's website.
        </Typography>
      </div>
    </Grid>
    <StyledGrid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FieldData>Score Send Code: <FieldValue>{update.di?.diCode}</FieldValue></FieldData>
          <FieldRow>
            <Typography variant="body1">Institution Name</Typography>
            {(readOnly && isChanged('data.org.name')) &&
              <div><i className={getChangeColor('data.org.name')}>Current: {getCurrent('data.org.name') || '(empty)'}</i></div>
            }
            <Input
              className={readOnly ? getChangeColor('data.org.name') : ''}
              disabled={readOnly}
              validation={hasError('data.org.name') ? 'error' : undefined}
              validationMsg={getError('data.org.name')}
              onChange={(e: any) => { setName(e.target.value); onFieldChange(); }}
              value={name} />
          </FieldRow>
          {update.org?.parentOrgId && <FieldData>Parent Org: <FieldValue>{`${update.org?.parentOrgName} (${update.org?.parentOrgId})`}</FieldValue></FieldData>}
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '4px' }}>
              Institution Type (Check all that apply)
            </FieldLabel>
            <Checkboxes
              readOnly={readOnly}
              error={getError('data.appForm.instType')}
              value={instType}
              options={TYPE_OPTIONS.map((val: string) => ({ key: val, label: val }))}
              onChange={(values: string[]) => {
                setInstType(values);
                //clear fields if option unchecked 
                setInstTypeOther(values.includes(KEY_OTHER) ? instTypeOther : undefined);
                setStateRep(values.includes(KEY_US_GOV) ? stateRep : undefined);
                setCongressDistrict(values.includes(KEY_US_GOV) ? congressDistrict : undefined);
                setIncumbent(values.includes(KEY_US_GOV) ? incumbent : undefined);
                onFieldChange();
              }}
            />
          </FieldRow>
          {instType.includes(KEY_OTHER) && (
            <FieldRow>
              <FieldLabel variant="body1">Specify the institution type that isn't represented in the options:</FieldLabel>
              <Input
                disabled={readOnly}
                value={instTypeOther}
                validation={hasError('data.appForm.instTypeOther') ? 'error' : undefined}
                validationMsg={getError('data.appForm.instTypeOther')}
                onChange={(e: any) => { setInstTypeOther(e.target.value); onFieldChange(); }}
              />
            </FieldRow>
          )}
          {instType.includes('US Government Official') && (
            <FieldRow>
              <Typography variant="body1">State Represented</Typography>
              <Dropdown
                disabled={readOnly}
                values={usStateOptions.filter((item) => item.primary === true).map((option) => ({ key: option.value, label: option.label }))}
                validation={hasError('data.appForm.stateRep') ? 'error' : undefined}
                validationMsg={getError('data.appForm.stateRep')}
                onChange={(e) => { setStateRep(e.target.value); onFieldChange(); }}
                value={stateRep}
              />
              <FieldLabel variant="body1">Congressional District</FieldLabel>
              <Input
                disabled={readOnly}
                value={congressDistrict}
                validation={hasError('data.appForm.congressDistrict') ? 'error' : undefined}
                validationMsg={getError('data.appForm.congressDistrict')}
                onChange={(e: any) => { setCongressDistrict(e.target.value); onFieldChange(); }}
              />
              <FieldLabel variant="body1">Predecessor</FieldLabel>
              <Input
                disabled={readOnly}
                value={incumbent}
                validation={hasError('data.appForm.incumbent') ? 'error' : undefined}
                validationMsg={getError('data.appForm.incumbent')}
                onChange={(e: any) => { setIncumbent(e.target.value); onFieldChange(); }}
              />
            </FieldRow>
          )}
          <FieldRow>
            <FieldLabel variant="body1">Which of the following best describes the level of your institution?</FieldLabel>
            {(readOnly && isChanged('org.instLvlCd')) &&
              <div><i className={getChangeColor('org.instLvlCd')}>
                Current: {instLvlOptions.filter(option => getCurrent('org.instLvlCd') && option.value === getCurrent('org.instLvlCd').toString())[0]?.label || '(empty)'}
              </i></div>
            }
            <Dropdown
              className={readOnly ? getChangeColor('org.instLvlCd') : ''}
              disabled={readOnly}
              values={instLvlOptions.map((option) => ({ key: option.value, label: option.label }))}
              validation={hasError('data.org.instLvlCd') ? 'error' : undefined}
              validationMsg={getError('data.org.instLvlCd')}
              onChange={(e) => { setInstLvlCd(parseInt(e.target.value)); onFieldChange(); }}
              value={instLvlCd?.toString()}
            />
          </FieldRow>
          <FieldRow>
            {(readOnly && isChanged('org.phone.formatted')) &&
              <div><i className={getChangeColor('org.phone.formatted')}>Current: {getCurrent('org.phone.formatted') || '(empty)'}</i></div>
            }
            <InputPhoneInt
              disabled={readOnly}
              label='General Institution Phone'
              className={readOnly ? getChangeColor('data.org.phone.formatted') : ''}
              validation={hasError('data.org.phone.formatted') || hasError('data.org.phone') ? 'error' : undefined}
              validationMsg={getError('data.org.phone.formatted') || getError('data.org.phone')}
              defaultCountry={defaultCountry} defaultValue={phone} onChange={(e) => { setPhone(e.target.value); onFieldChange(); }} />
          </FieldRow>
          <FieldRow>
            <Typography variant="body1">General Institution Fax</Typography>
            {(readOnly && isChanged('org.fax.formatted')) &&
              <div><i className={getChangeColor('org.fax.formatted')}>Current: {getCurrent('org.fax.formatted') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={readOnly ? getChangeColor('org.fax.formatted') : ''}
              validation={hasError('data.org.fax.formatted') ? 'error' : undefined}
              validationMsg={getError('data.org.fax.formatted')}
              onChange={(e: any) => { setFax(e.target.value); onFieldChange(); }} value={fax} />
          </FieldRow>
          <FieldRow>
            <Typography variant="body1">General Institution Website</Typography>
            {(readOnly && isChanged('org.website')) &&
              <div><i className={getChangeColor('org.website')}>Current: {getCurrent('.org.website') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={readOnly ? getChangeColor('data.org.website') : ''}
              validation={hasError('data.org.website') ? 'error' : undefined}
              validationMsg={getError('data.org.website')}
              onChange={(e: any) => { setWebsite(e.target.value); onFieldChange(); }} value={website} />
          </FieldRow>
          <FieldRow>
            <Typography variant="body1">General Institution Email</Typography>
            {(readOnly && isChanged('org.email')) &&
              <div><i className={getChangeColor('org.email')}>Current: {getCurrent('org.email') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={readOnly ? getChangeColor('org.email') : ''}
              validation={hasError('data.org.email') ? 'error' : undefined}
              validationMsg={getError('data.org.email')}
              onChange={(e: any) => { setEmail(e.target.value); onFieldChange(); }} value={email} />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          <FieldRow><strong>Institution Visiting Address</strong></FieldRow>
          <AddressFields
            readOnly={readOnly}
            showDiff={readOnly}
            errors={getFieldError('data.org.visitingAddress')}
            address={visitingAddress}
            original={getCurrent('org.visitingAddress') || {}}
            onUpdate={(address) => { setVisitingAddress(address); onFieldChange(); }} />
        </Grid>
      </Grid>
    </StyledGrid>
    <StyledGrid>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '4px' }}>
              Program Scores Accepted (Check all that apply):
            </FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}>
              {(readOnly && isChanged('di.satEligibilityInd')) &&
                <div><i className={getChangeColor('di.satEligibilityInd')}>Current: {getCurrent('di.satEligibilityInd').toString()}</i></div>
              }
              <Checkbox
                disabled={readOnly}
                onChange={(value: any) => {
                  setSatEligibilityInd(!satEligibilityInd);
                  //Don't clear contact fields if unchecked
                  !satEligibilityInd && setSatDisclaimer(false); setSatClosureDate(undefined); setMerge(false);
                  onFieldChange();
                }}
                label={'SAT'}
                name={'satEligibilityInd'}
                checked={satEligibilityInd}
              />
              {(readOnly && isChanged('di.apEligibilityInd')) &&
                <div><i className={getChangeColor('di.apEligibilityInd')}>Current: {getCurrent('di.apEligibilityInd').toString()}</i></div>
              }
              <Checkbox
                disabled={readOnly}
                onChange={() => {
                  setApEligibilityInd(!apEligibilityInd);
                  //Don't clear contact fields if unchecked
                  !apEligibilityInd && setApDisclaimer(false); setApClosureDate(undefined); setMerge(false);
                  onFieldChange();
                }}
                label={'AP'}
                name={'apEligibilityInd'}
                checked={apEligibilityInd}
              />
            </div>
          </FieldRow>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body1">Checking the program box indicates your institution will now accept scores for the program indicated.</Typography>
          <Typography variant="body1">Unchecking the program box indicates your institution will no longer accept scores for the program indicated.</Typography>
        </Grid>
      </Grid>
    </StyledGrid>
    {/* SAT CHECKED */}
    {satEligibilityInd && <>
      <StyledGrid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Title>SAT Program Contact Information</Title>
            <ContactFields
              readOnly={readOnly}
              showDiff={readOnly}
              item={satContact || {}}
              original={initial.di?.satContact}
              onUpdate={(values) => {
                setSatContact(values);
              }}
              errors={getFieldError('data.di.satContact')}
            />
          </Grid>
        </Grid>
      </StyledGrid></>
    }
    {/* SAT UNCHECKED */}
    {initial.di?.satEligibilityInd && !satEligibilityInd && <>
      <StyledGrid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DatePicker disableButton={readOnly} onSelect={(event) => {
                  if (event && event.dateObj && event.dateObj.start) {
                    const select: Date = new Date(event.dateObj.start);
                    const date = `${select.getMonth() + 1}/${select.getDate()}/${select.getFullYear()}`;
                    setSatClosureDate(date);
                  } else {
                    setSatClosureDate(undefined);
                  }
                }} singleDate startDate={satProgramDeactivation.closureDate} />
                {hasError('data.appForm.satProgramDeactivation.closureDate') &&
                  <span className='cb-input-helper cb-validation-error'>{getErrorMessages('data.appForm.satProgramDeactivation.closureDate').join("\n")}</span>
                }
              </div>
              <Typography style={{ marginLeft: '10px' }} variant="body1">Please indicate on which date your institution would like to discontinue receipt of SAT exam scores. </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Checkbox
                disabled={readOnly}
                onChange={() => {
                  setSatDisclaimer(!satDisclaimer);
                  onFieldChange();
                }}
                label={''}
                name={'data.appForm.satProgramDeactivation.deactivateDisclaimer'}
                checked={satProgramDeactivation.deactivateDisclaimer}
              />
              <FieldLabel>I understand by submitting this request that, my organization will no longer be able to access, view, or extract student data, including score data beyond the date indicated above.
                I confirm that my organization has extracted all score and student data from College Board before submitting this form.</FieldLabel>
            </div>
            {hasError('data.appForm.satProgramDeactivation.deactivateDisclaimer') &&
              <span className='cb-input-helper cb-validation-error'>{getErrorMessages('data.appForm.satProgramDeactivation.deactivateDisclaimer').join("\n")}</span>
            }
          </Grid>
        </Grid>
      </StyledGrid>
    </>
    }
    {/* AP CHECKED */}
    {apEligibilityInd && <>
      <StyledGrid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Title>AP Program Contact Information</Title>
            <ContactFields
              readOnly={readOnly}
              showDiff={readOnly}
              item={apContact || {}}
              original={initial.di?.apContact}
              onUpdate={(values) => {
                setApContact(values);
              }}
              errors={getFieldError('data.di.apContact')}
            />
          </Grid>
          <Grid item xs={6}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Checkbox
                disabled={readOnly}
                label={''}
                style={{ display: 'inline-block' }}
                checked={!!apAddress}
                onChange={() => {
                  if (!apAddress) {
                    setApAddress({
                      countryIsoCd: (apAddress && apAddress['countryIsoCd']) || 'US',
                      stateCd: apAddress && apAddress['stateCd'],
                      province: apAddress && apAddress['province'],
                    });
                  } else {
                    setApAddress(undefined);
                  }
                }}
              />
              <FieldLabel>
                Check this box to add a different address for mailed AP communications.
                <br />
                Unchecking this box indicates that mailed AP communications can be delivered to the visiting address provided.
              </FieldLabel>
            </div>
            {hasError('data.di.apAddress') && (
              <span className="cb-input-helper cb-validation-error">{getError('data.di.apAddress')}</span>
            )}
            {apAddress && (
              <>
                <FieldRow>
                  <strong>AP Mailing Address</strong>
                </FieldRow>
                <AddressFields
                  readOnly={readOnly}
                  showDiff={readOnly}
                  addressNote={''}
                  errors={getFieldError('data.di.apAddress')}
                  address={apAddress}
                  original={getCurrent('data.di.apAddress') || {}}
                  onUpdate={(address) => {
                    setApAddress(address);
                  }}
                />
              </>
            )}
          </Grid>
        </Grid>
      </StyledGrid>
    </>}
    {/* AP UNCHECKED */}
    {initial.di?.apEligibilityInd && !apEligibilityInd && <>
      <StyledGrid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DatePicker disableButton={readOnly} onSelect={(event) => {
                  if (event && event.dateObj && event.dateObj.start) {
                    const select: Date = new Date(event.dateObj.start);
                    const date = `${select.getMonth() + 1}/${select.getDate()}/${select.getFullYear()}`;
                    setApClosureDate(date);
                  } else {
                    setApClosureDate(undefined);
                  }
                }} singleDate startDate={apClosureDate} />
                {hasError('data.appForm.apProgramDeactivation.closureDate') &&
                  <span className='cb-input-helper cb-validation-error'>{getErrorMessages('data.appForm.apProgramDeactivation.closureDate').join("\n")}</span>
                }
              </div>
              <Typography style={{ marginLeft: '10px' }} variant="body1">Please indicate on which date your institution would like to discontinue being listed as a score recipient for AP Student exam scores.</Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Checkbox
                disabled={readOnly}
                onChange={() => {
                  setApDisclaimer(!apDisclaimer);
                  onFieldChange();
                }}
                label={''}
                name={'data.appForm.apProgramDeactivation.deactivateDisclaimer'}
                checked={apDisclaimer}
              />
              <FieldLabel>I understand by submitting this request that my organization will no longer be available for students to select as an AP exam score recipient.</FieldLabel>
            </div>
            {hasError('data.appForm.apProgramDeactivation.deactivateDisclaimer') &&
              <span className='cb-input-helper cb-validation-error'>{getErrorMessages('data.appForm.apProgramDeactivation.deactivateDisclaimer').join("\n")}</span>
            }
          </Grid>
        </Grid>
      </StyledGrid>
    </>
    }
    {/* BOTH  UNCHECKED */}
    {!satEligibilityInd && !apEligibilityInd && <>
      <StyledGrid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FieldRow>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <FieldLabel style={{ marginRight: '5px' }}>My institution is merging with another institution </FieldLabel>
                <ToggleSwitch on={isMerge} disabled={readOnly} offLabel={'No'} onLabel={'Yes'} onChange={(value: boolean) => {
                  setMerge(value);
                  value ? setMergeAddress({
                    countryIsoCd: visitingAddress.countryIsoCd,
                    stateCd: visitingAddress.stateCd,
                    province: visitingAddress.province
                  })
                    : setMergeAddress(undefined); setMergedDICd(undefined); setMergeName(undefined);
                  onFieldChange();
                }} />
              </div>
            </FieldRow>
            {isMerge &&
              <FieldRow>
                <FieldLabel variant="body1">Merging Score Send Code </FieldLabel>
                <Input
                  disabled={readOnly}
                  value={mergedDICd}
                  validation={hasError('data.appForm.merge') || hasError('data.appForm.merge.diCode') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.merge') || getError('data.appForm.merge.diCode')}
                  onChange={(e: any) => { setMergedDICd(e.target.value); onFieldChange(); }}
                />
                <FieldLabel style={{ textAlign: 'center', margin: '5px' }}> OR </FieldLabel>
                <FieldLabel variant="body1">Merging Institution Name </FieldLabel>
                {(readOnly && isChanged('appForm.merge.name')) &&
                  <div><i className={getChangeColor('appForm.merge.name')}>Current: {getCurrent('appForm.merge.name') || '(empty)'}</i></div>
                }
                <Input
                  disabled={readOnly}
                  value={mergeName}
                  validation={hasError('data.appForm.merge') || hasError('data.appForm.merge.name') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.merge') || getError('data.appForm.merge.name')}
                  onChange={(e: any) => { setMergeName(e.target.value); onFieldChange(); }}
                />
                <FieldRow><strong>Merging Institution Visiting Address</strong></FieldRow>
                <AddressFields
                  readOnly={readOnly}
                  showDiff={readOnly}
                  errors={getFieldError('data.appForm.merge.address')}
                  address={mergeAddress}
                  original={getCurrent('appForm.merge.address') || {}}
                  onUpdate={(address) => { setMergeAddress(address); onFieldChange(); }} />
              </FieldRow>
            }
          </Grid>
        </Grid>
      </StyledGrid>
    </>}
    <StyledGrid>
      <Grid>
        <Grid item xs={12}>
          <FieldRow>
            <Typography variant="body1">Additional Comments</Typography>
            <TextArea
              disabled={readOnly}
              className={readOnly ? getChangeColor('submitterComment') : ''}
              validation={hasError('data.submitterComment') ? 'error' : undefined}
              validationMsg={getError('data.submitterComment')}
              onChange={(e: any) => { setSubmitterComment(e.target.value); onFieldChange(); }} value={submitterComment} />
          </FieldRow>
        </Grid>
      </Grid>
    </StyledGrid>
    {/* TODO: disclaimer doesnt reset. copy create instead of doing it like this?*/}
    <StyledGrid style={{ marginBottom: "30px" }}>
      <h4 style={{ marginBottom: "10px" }}>Disclaimer</h4>
      <FieldRow>
        <Typography variant="body1" style={{ marginBottom: '5px' }}>By submitting this request I confirm that all of the information provided is true and accurately describes the organization named on this form and have the authority to make this request. I understand that if any of the information is false, deactivation of the school code and/or legal action may result.</Typography>
        <Checkbox disabled={readOnly} label='Accept' style={{ display: 'inline-block' }} checked={disclaimer} onChange={() => {
          setDisclaimer(!disclaimer);
          onFieldChange();
        }} />
        {hasError('meta.disclaimer') &&
          <span className='cb-input-helper cb-validation-error'>You must agree to the disclaimer</span>
        }
      </FieldRow>
      <FieldRow>
        <Typography variant="body1">Submitter</Typography>
        <Input
          disabled={readOnly}
          validation={hasError('meta.submitter') ? 'error' : undefined}
          validationMsg={getError('meta.submitter')}
          onChange={(e: any) => { setSubmitter(e.target.value); onFieldChange(); }} value={submitter} />
      </FieldRow>
    </StyledGrid>
    {!readOnly &&
      <PrimaryButton style={{ width: '100%', marginBottom: '10px' }} onClick={submit} disabled={submitting}>Submit</PrimaryButton>
    }
    <Button style={{ width: '100%' }} onClick={() => source === 'SEARCH' ? navigate(APP_URLS.DISEARCH, { state: { search: searchParams } }) : navigate(APP_URLS.DIHOME)} disabled={submitting}>{readOnly ? 'Close' : 'Cancel'}</Button>
  </form>;
};